// Tiny dependency-free i18n runtime.
//
// Responsibilities:
//  - hold the active locale (persisted in localStorage, detected from the
//    browser on first visit),
//  - resolve translation keys with interpolation and an English fallback,
//  - apply translations to static DOM marked with data-i18n* attributes,
//  - notify subscribers (the app re-renders dynamic content) on locale change.
//
// All browser globals are accessed through guards so this module also imports
// cleanly under a non-DOM test runner (vitest's default node environment).

import { MESSAGES, DEFAULT_LOCALE, SUPPORTED_LOCALES } from './messages.js';
import { lookupCountryName } from './countryNames.js';
import { I18N_CONFIG, isMultiLocaleEnabled } from '../config.js';
import { STOREFRONTS } from '../data/storefronts.js';

const STORAGE_KEY = 'appradar.lang';

const listeners = new Set();
let current = detectLocale();

/**
 * The locales currently exposed in public UI controls. The app always ships
 * every catalog in SUPPORTED_LOCALES; explicit locale routes can still render a
 * hidden catalog even when it is not exposed in the switcher.
 * @returns {string[]}
 */
export function enabledLocales() {
  if (!isMultiLocaleEnabled()) return [DEFAULT_LOCALE];
  const configured = Array.isArray(I18N_CONFIG.publicLocales)
    ? I18N_CONFIG.publicLocales.filter((locale) => SUPPORTED_LOCALES.includes(locale))
    : [];
  return configured.length ? [...new Set(configured)] : [...SUPPORTED_LOCALES];
}

/**
 * Map an arbitrary BCP-47 language tag to one of our supported locales.
 * Chinese variants preserve script where the browser tag carries enough signal:
 * zh-CN / zh-Hans map to Simplified, zh-TW / zh-HK / zh-Hant map to Traditional.
 * @param {string|null|undefined} tag
 * @returns {string|null} a supported locale, or null if unmatched
 */
function matchLocale(tag) {
  if (!tag) return null;
  const raw = String(tag);
  const lower = raw.toLowerCase().replace(/_/g, '-');
  if (SUPPORTED_LOCALES.includes(raw)) return raw;
  if (lower.startsWith('ja')) return 'ja';
  if (lower === 'zh' || lower.startsWith('zh-')) return matchChineseLocale(lower);
  if (lower.startsWith('en')) return 'en';
  return null;
}

function matchLocaleArgument(tag) {
  if (!tag) return null;
  const raw = String(tag);
  const lower = raw.toLowerCase().replace(/_/g, '-');
  if (SUPPORTED_LOCALES.includes(raw)) return raw;
  if (lower === 'ja' || lower.startsWith('ja-')) return 'ja';
  if (lower === 'zh' || lower.startsWith('zh-')) return matchChineseLocale(lower);
  if (lower === 'en' || lower.startsWith('en-')) return 'en';
  return null;
}

function matchChineseLocale(lowerTag) {
  if (lowerTag.includes('hant') || /(^|-)tw($|-)|(^|-)hk($|-)|(^|-)mo($|-)/.test(lowerTag)) {
    return 'zh-Hant';
  }
  return 'zh-CN';
}

/**
 * Locale override encoded in the first URL path segment for shareable pages
 * like /zh and /zh-CN.
 * @param {string|null|undefined} pathname
 * @returns {string|null}
 */
export function localeFromPathname(pathname) {
  const first = String(pathname ?? '')
    .split('/')
    .filter(Boolean)[0];
  if (!first) return null;
  const lower = first.toLowerCase();
  if (lower === 'zh' || lower === 'cn' || lower === 'zh-cn' || lower === 'zh-hans') return 'zh-CN';
  if (lower === 'zh-hant' || lower === 'zh-tw' || lower === 'zh-hk') return 'zh-Hant';
  if (lower === 'ja' || lower === 'jp') return 'ja';
  if (lower === 'en') return 'en';
  return null;
}

/**
 * Decide the initial locale: explicit locale route first, then stored
 * preference, browser languages, and finally the default.
 * @returns {string}
 */
function detectLocale() {
  // Only ever auto-select a locale we currently expose. When multi-locale is
  // off this collapses to English, so a stale stored preference or a JA/zh
  // browser does not resurface a hidden language.
  const allowed = enabledLocales();
  const pick = (tag) => {
    const matched = matchLocale(tag);
    return matched && allowed.includes(matched) ? matched : null;
  };

  // 0. Explicit locale route, e.g. /zh or /zh-CN.
  if (typeof window !== 'undefined') {
    const routed = matchLocale(localeFromPathname(window.location?.pathname));
    if (routed) return routed;
  }

  // 1. Stored preference.
  try {
    if (typeof localStorage !== 'undefined') {
      const stored = pick(localStorage.getItem(STORAGE_KEY));
      if (stored) return stored;
    }
  } catch {
    // localStorage can throw (private mode / disabled) — ignore and fall through.
  }

  // 2. Browser languages.
  if (typeof navigator !== 'undefined') {
    const langs = navigator.languages && navigator.languages.length
      ? navigator.languages
      : [navigator.language];
    for (const lang of langs) {
      const matched = pick(lang);
      if (matched) return matched;
    }
  }

  // 3. Default.
  return DEFAULT_LOCALE;
}

/** @returns {string} the active locale */
export function getLocale() {
  return current;
}

/** @returns {string[]} the locales the app ships */
export function supportedLocales() {
  return [...SUPPORTED_LOCALES];
}

/**
 * Global interpolation values that are derived from runtime app data rather
 * than duplicated in locale copy.
 * @returns {{countryCount: number}}
 */
export function coverageParams() {
  return { countryCount: STOREFRONTS.length };
}

/**
 * Switch the active locale. No-op if unsupported or unchanged. Persists the
 * choice, updates <html lang>, and notifies subscribers.
 * @param {string} locale
 */
export function setLocale(locale) {
  if (!SUPPORTED_LOCALES.includes(locale) || locale === current) return;
  current = locale;
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, locale);
    }
  } catch {
    // Persisting is best-effort.
  }
  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.setAttribute('lang', locale);
  }
  for (const fn of listeners) {
    try {
      fn(locale);
    } catch {
      // A misbehaving listener must not break locale switching.
    }
  }
}

/**
 * Subscribe to locale changes.
 * @param {(locale: string) => void} fn
 * @returns {() => void} unsubscribe
 */
export function onLocaleChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Translate a key for the active locale, with English fallback and `{param}`
 * interpolation. Unknown keys return the key itself so missing strings are
 * obvious rather than blank.
 * @param {string} key
 * @param {Record<string, string|number>} [params]
 * @returns {string}
 */
export function t(key, params) {
  const catalog = MESSAGES[current] || MESSAGES[DEFAULT_LOCALE];
  let str = catalog[key];
  if (str === undefined) str = MESSAGES[DEFAULT_LOCALE][key];
  if (str === undefined) return key;
  const mergedParams = { ...coverageParams(), ...(params ?? {}) };
  if (mergedParams) {
    str = str.replace(/\{(\w+)\}/g, (whole, name) =>
      Object.prototype.hasOwnProperty.call(mergedParams, name) ? String(mergedParams[name]) : whole,
    );
  }
  return str;
}

/**
 * Localized display name for a storefront country code. The second argument may
 * be a locale (`countryName('jp', 'ja')`) or, for older call sites, an English
 * fallback (`countryName('jp', 'Japan')`). Missing locale entries fall back to
 * English, then to the explicit fallback, then to the code.
 * @param {string} code
 * @param {string} [localeOrFallback]
 * @param {string} [fallback]
 * @returns {string}
 */
export function countryName(code, localeOrFallback, fallback) {
  const matchedLocale = matchLocaleArgument(localeOrFallback);
  const locale = matchedLocale ?? current;
  const explicitFallback = matchedLocale ? fallback : localeOrFallback;
  return lookupCountryName(code, locale, explicitFallback);
}

/**
 * Apply translations to static DOM. Elements opt in with:
 *   data-i18n="key"                  -> textContent
 *   data-i18n-placeholder="key"      -> placeholder attribute
 *   data-i18n-aria-label="key"       -> aria-label attribute
 *   data-i18n-content="key"          -> content attribute (meta tags)
 * Safe to call repeatedly (e.g. on every locale change).
 * @param {ParentNode} [root=document]
 */
export function applyStaticTranslations(root) {
  if (typeof document === 'undefined') return;
  const scope = root || document;

  scope.querySelectorAll('[data-i18n]').forEach((el) => {
    el.textContent = t(el.getAttribute('data-i18n'));
  });
  const attrMap = {
    'data-i18n-placeholder': 'placeholder',
    'data-i18n-aria-label': 'aria-label',
    'data-i18n-content': 'content',
    'data-i18n-title': 'title',
  };
  for (const [dataAttr, domAttr] of Object.entries(attrMap)) {
    scope.querySelectorAll(`[${dataAttr}]`).forEach((el) => {
      el.setAttribute(domAttr, t(el.getAttribute(dataAttr)));
    });
  }
}
